import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaPen,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import {
  deleteProRecommendationCandidate,
  getProRecommendationCandidates,
  saveProRecommendationCandidate,
} from "../../api/project";
import {
  ActionCell,
  ActionGroup,
  Card,
  CardBody,
  CardHeader,
  ColorCell,
  ColorInputRow,
  ColorPicker,
  ColorSwatch,
  EmptyState,
  ErrorText,
  Field,
  FormGrid,
  FullWidthField,
  Hint,
  Input,
  Label,
  ModalButtons,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Muted,
  RecommendationBody,
  RecommendationPageLayout,
  Required,
  SearchInput,
  SearchRow,
  SmallButton,
  Table,
  TableWrapper,
  TextArea,
  Title,
  ToolbarRow,
} from "./RecommendationCandidates.style";

const EMPTY_FORM = {
  id: "",
  user_id: "",
  user_name: "",
  show_user_name: "",
  hex_code: "",
  hex_font_code: "",
  memo: "",
};

const asText = (value) =>
  value === null || value === undefined ? "" : String(value);

const displayValue = (value) => {
  const text = asText(value).trim();
  return text || "-";
};

const formatDateTime = (value) => {
  const text = asText(value).trim();
  if (!text) return "-";

  const date = /^\d+$/.test(text)
    ? new Date(Number(text))
    : new Date(text.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return text;

  return date
    .toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(/\//g, "-");
};

const isValidHex = (value) => {
  const text = asText(value).trim();
  return !text || /^#?(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text);
};

const toColorInputValue = (value) => {
  const text = asText(value).trim();
  const match = text.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return "#ffffff";

  let hex = match[1];
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((character) => character + character)
      .join("");
  }
  return `#${hex}`;
};

const swatchColor = (value) => {
  const text = asText(value).trim();
  if (!text) return "#f5f5f5";

  const withHash = text.startsWith("#") ? text : `#${text}`;
  return isValidHex(text) ? withHash : "#eeeeee";
};

export default function RecommendationCandidates() {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const requestIdRef = useRef(0);

  const loadCandidates = useCallback(async (query = "") => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await getProRecommendationCandidates(
        query ? { keyword: query } : {},
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      const data = response && response.data;
      setCandidates(Array.isArray(data) ? data : []);
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      console.error("Failed to load recommendation candidates:", error);
      setCandidates([]);
      const message =
        error && error.message
          ? error.message
          : "候補者一覧の取得に失敗しました";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!sessionStorage.getItem("userId")) {
      navigate("/");
      return;
    }
    loadCandidates(searchKeyword);
  }, [loadCandidates, navigate, searchKeyword]);

  const handleSearch = (event) => {
    event.preventDefault();
    setSearchKeyword(keyword.trim());
  };

  const handleReset = () => {
    setKeyword("");
    setSearchKeyword("");
  };

  const handleChange = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const openNew = () => {
    setEditingId("");
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEdit = (candidate) => {
    setEditingId(asText(candidate.id));
    setForm({
      id: asText(candidate.id),
      user_id: asText(candidate.user_id),
      user_name: asText(candidate.user_name),
      show_user_name: asText(candidate.show_user_name),
      hex_code: asText(candidate.hex_code),
      hex_font_code: asText(candidate.hex_font_code),
      memo: asText(candidate.memo),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const userId = form.user_id.trim();
    if (!userId) {
      toast.warning("ユーザーIDを入力してください");
      return;
    }
    if (userId.length > 100) {
      toast.warning("ユーザーIDは100文字以内で入力してください");
      return;
    }
    if (form.user_name.length > 100) {
      toast.warning("ユーザー名は100文字以内で入力してください");
      return;
    }
    if (form.show_user_name.length > 100) {
      toast.warning("表示用ユーザー名は100文字以内で入力してください");
      return;
    }
    if (form.hex_code.length > 20 || form.hex_font_code.length > 20) {
      toast.warning("HEXコードは20文字以内で入力してください");
      return;
    }
    if (!isValidHex(form.hex_code) || !isValidHex(form.hex_font_code)) {
      toast.warning("HEXコードは3～8桁の色コードで入力してください");
      return;
    }
    if (form.memo.length > 100) {
      toast.warning("メモは100文字以内で入力してください");
      return;
    }

    setSaving(true);
    try {
      await saveProRecommendationCandidate({
        id: editingId,
        user_id: userId,
        user_name: form.user_name.trim(),
        show_user_name: form.show_user_name.trim(),
        hex_code: form.hex_code.trim(),
        hex_font_code: form.hex_font_code.trim(),
        memo: form.memo.trim(),
      });
      toast.success(editingId ? "候補者情報を更新しました" : "候補者を登録しました");
      setModalOpen(false);
      await loadCandidates(searchKeyword);
    } catch (error) {
      console.error("Failed to save recommendation candidate:", error);
      toast.error(
        "保存に失敗しました: " + (error.message || "サーバーエラー"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (candidate) => {
    const name = candidate.show_user_name || candidate.user_name || candidate.user_id;
    if (!window.confirm(`候補者「${name}」を削除しますか？`)) {
      return;
    }

    setDeletingId(asText(candidate.id));
    try {
      await deleteProRecommendationCandidate(candidate.id);
      toast.success("候補者を削除しました");
      await loadCandidates(searchKeyword);
    } catch (error) {
      console.error("Failed to delete recommendation candidate:", error);
      toast.error(
        "削除に失敗しました: " + (error.message || "サーバーエラー"),
      );
    } finally {
      setDeletingId("");
    }
  };

  return (
    <MainLayout>
      <RecommendationPageLayout>
        <RecommendationBody>
          <Title>Proおすすめ候補者管理</Title>

          <ToolbarRow>
            <SmallButton type="button" variant="success" onClick={openNew}>
              <FaPlus /> 新規登録
            </SmallButton>
            <SmallButton
              type="button"
              variant="dark"
              onClick={() => loadCandidates(searchKeyword)}
              disabled={isLoading}
            >
              <FaSyncAlt /> {isLoading ? "読み込み中..." : "リフレッシュ"}
            </SmallButton>
            <Muted>候補者は、Proおすすめアプリの設定データとして使用されます。</Muted>
          </ToolbarRow>

          <Card>
            <CardHeader>
              <span>候補者一覧（{candidates.length}件）</span>
              <Muted>検索対象：ID、ユーザーID、名前、表示名、メモ</Muted>
            </CardHeader>
            <CardBody>
              <SearchRow onSubmit={handleSearch} role="search">
                <SearchInput
                  type="search"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="ID、ユーザーID、名前、メモで検索"
                  maxLength={100}
                  aria-label="候補者を検索"
                />
                <SmallButton type="submit">
                  <FaSearch /> 検索
                </SmallButton>
                <SmallButton
                  type="button"
                  variant="secondary"
                  onClick={handleReset}
                >
                  クリア
                </SmallButton>
              </SearchRow>

              {errorMessage && <ErrorText role="alert">{errorMessage}</ErrorText>}

              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>ユーザーID</th>
                      <th>ユーザー名</th>
                      <th>表示用ユーザー名</th>
                      <th>背景色</th>
                      <th>字体色</th>
                      <th>メモ</th>
                      <th>作成日時</th>
                      <th>更新日時</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={10}>
                          <EmptyState>読み込み中...</EmptyState>
                        </td>
                      </tr>
                    ) : candidates.length === 0 ? (
                      <tr>
                        <td colSpan={10}>
                          <EmptyState>
                            候補者が登録されていません。新規登録から追加してください。
                          </EmptyState>
                        </td>
                      </tr>
                    ) : (
                      candidates.map((candidate) => (
                        <tr key={candidate.id}>
                          <td>{displayValue(candidate.id)}</td>
                          <td>{displayValue(candidate.user_id)}</td>
                          <td>{displayValue(candidate.user_name)}</td>
                          <td>{displayValue(candidate.show_user_name)}</td>
                          <td>
                            <ColorCell>
                              <ColorSwatch
                                style={{
                                  backgroundColor: swatchColor(candidate.hex_code),
                                }}
                              />
                              <span>{displayValue(candidate.hex_code)}</span>
                            </ColorCell>
                          </td>
                          <td>
                            <ColorCell>
                              <ColorSwatch
                                style={{
                                  backgroundColor: swatchColor(
                                    candidate.hex_font_code,
                                  ),
                                }}
                              />
                              <span>
                                {displayValue(candidate.hex_font_code)}
                              </span>
                            </ColorCell>
                          </td>
                          <td title={asText(candidate.memo)}>
                            {displayValue(candidate.memo)}
                          </td>
                          <td>{formatDateTime(candidate.created_at)}</td>
                          <td>{formatDateTime(candidate.updated_at)}</td>
                          <ActionCell>
                            <ActionGroup>
                              <SmallButton
                                type="button"
                                onClick={() => openEdit(candidate)}
                                disabled={Boolean(deletingId)}
                                aria-label="候補者を編集"
                              >
                                <FaPen /> 編集
                              </SmallButton>
                              <SmallButton
                                type="button"
                                variant="danger"
                                onClick={() => handleDelete(candidate)}
                                disabled={Boolean(deletingId)}
                                aria-label="候補者を削除"
                              >
                                <FaTrash /> 削除
                              </SmallButton>
                            </ActionGroup>
                          </ActionCell>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableWrapper>
            </CardBody>
          </Card>
        </RecommendationBody>
      </RecommendationPageLayout>

      {modalOpen && (
        <ModalOverlay
          onClick={(event) => {
            if (event.target === event.currentTarget && !saving) {
              setModalOpen(false);
            }
          }}
        >
          <ModalContent onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <span>{editingId ? "候補者 編集" : "候補者 新規登録"}</span>
              <SmallButton
                type="button"
                variant="secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                × 閉じる
              </SmallButton>
            </ModalHeader>

            <FormGrid>
              <Field>
                <Label htmlFor="recommendation-id">ID</Label>
                <Input
                  id="recommendation-id"
                  value={form.id}
                  readOnly
                  placeholder="自動採番"
                />
                <Hint>登録時に自動採番されます。</Hint>
              </Field>

              <Field>
                <Label htmlFor="recommendation-user-id">
                  ユーザーID
                  <Required>必須</Required>
                </Label>
                <Input
                  id="recommendation-user-id"
                  value={form.user_id}
                  onChange={(event) =>
                    handleChange("user_id", event.target.value)
                  }
                  maxLength={100}
                  placeholder="例：pro_user_001"
                />
              </Field>

              <Field>
                <Label htmlFor="recommendation-user-name">ユーザー名</Label>
                <Input
                  id="recommendation-user-name"
                  value={form.user_name}
                  onChange={(event) =>
                    handleChange("user_name", event.target.value)
                  }
                  maxLength={100}
                  placeholder="例：山田 太郎"
                />
              </Field>

              <Field>
                <Label htmlFor="recommendation-show-user-name">
                  表示用ユーザー名
                </Label>
                <Input
                  id="recommendation-show-user-name"
                  value={form.show_user_name}
                  onChange={(event) =>
                    handleChange("show_user_name", event.target.value)
                  }
                  maxLength={100}
                  placeholder="例：山田プロ"
                />
              </Field>

              <Field>
                <Label htmlFor="recommendation-background-color">
                  背景色HEXコード
                </Label>
                <ColorInputRow>
                  <ColorPicker
                    type="color"
                    value={toColorInputValue(form.hex_code)}
                    onChange={(event) =>
                      handleChange("hex_code", event.target.value.toUpperCase())
                    }
                    aria-label="背景色を選択"
                  />
                  <Input
                    id="recommendation-background-color"
                    value={form.hex_code}
                    onChange={(event) =>
                      handleChange("hex_code", event.target.value)
                    }
                    maxLength={20}
                    placeholder="#RRGGBB"
                  />
                </ColorInputRow>
              </Field>

              <Field>
                <Label htmlFor="recommendation-font-color">字体色HEXコード</Label>
                <ColorInputRow>
                  <ColorPicker
                    type="color"
                    value={toColorInputValue(form.hex_font_code)}
                    onChange={(event) =>
                      handleChange(
                        "hex_font_code",
                        event.target.value.toUpperCase(),
                      )
                    }
                    aria-label="字体色を選択"
                  />
                  <Input
                    id="recommendation-font-color"
                    value={form.hex_font_code}
                    onChange={(event) =>
                      handleChange("hex_font_code", event.target.value)
                    }
                    maxLength={20}
                    placeholder="#RRGGBB"
                  />
                </ColorInputRow>
              </Field>

              <FullWidthField>
                <Label htmlFor="recommendation-memo">メモ</Label>
                <TextArea
                  id="recommendation-memo"
                  value={form.memo}
                  onChange={(event) => handleChange("memo", event.target.value)}
                  maxLength={100}
                  placeholder="おすすめ理由などを入力（100文字以内）"
                  rows={3}
                />
              </FullWidthField>
            </FormGrid>

            <ModalButtons>
              <SmallButton
                type="button"
                variant="secondary"
                onClick={() => setModalOpen(false)}
                disabled={saving}
              >
                キャンセル
              </SmallButton>
              <SmallButton
                type="button"
                variant="success"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "保存中..." : "保存"}
              </SmallButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </MainLayout>
  );
}
